import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2, RefreshCw, Trash2, Upload } from 'lucide-react';
import {
  DOC_KINDS,
  deleteOrgDocument,
  listOrgDocuments,
  triggerIngest,
  uploadOrgDocument,
  uploadOrgDocumentFromText,
} from '../../utils/orgDocuments';

const STATUS_LABEL = {
  pending: 'Pending',
  processing: 'Processing…',
  ready: 'Ready',
  failed: 'Failed',
};

export default function OrgDocumentsPanel({ organizationId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [kind, setKind] = useState('bylaws');
  const [pasteTitle, setPasteTitle] = useState('Bylaws');
  const [pasteText, setPasteText] = useState('');
  const [showPaste, setShowPaste] = useState(false);

  const refresh = useCallback(async ({ clearError = false } = {}) => {
    if (!organizationId) return;
    setLoading(true);
    if (clearError) setError(null);
    try {
      const rows = await listOrgDocuments(organizationId);
      setDocs(rows);
    } catch (err) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void refresh({ clearError: true });
  }, [refresh]);

  // Poll while any doc is pending/processing (don't wipe Retry error banners)
  useEffect(() => {
    const active = docs.some((d) => d.status === 'pending' || d.status === 'processing');
    if (!active) return undefined;
    const t = setInterval(() => {
      void refresh({ clearError: false });
    }, 2500);
    return () => clearInterval(t);
  }, [docs, refresh]);

  // Clear indexing notice once nothing is still pending/processing
  useEffect(() => {
    if (!notice) return;
    const active = docs.some((d) => d.status === 'pending' || d.status === 'processing');
    if (!active && docs.some((d) => d.status === 'ready')) {
      setNotice(null);
    }
  }, [docs, notice]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !organizationId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const row = await uploadOrgDocument({ organizationId, file, kind });
      await refresh({ clearError: false });
      if (row?.ingestError) {
        setNotice(
          `Uploaded — indexing did not finish (${row.ingestError}). Use Retry if status stays Pending.`
        );
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
      await refresh({ clearError: false }).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const onPasteSubmit = async () => {
    if (!organizationId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const row = await uploadOrgDocumentFromText({
        organizationId,
        text: pasteText,
        kind,
        title: pasteTitle || 'Pasted bylaws',
      });
      setPasteText('');
      setShowPaste(false);
      await refresh({ clearError: false });
      if (row?.ingestError) {
        setNotice(
          `Uploaded — indexing did not finish (${row.ingestError}). Use Retry if status stays Pending.`
        );
      }
    } catch (err) {
      setError(err.message || 'Paste upload failed');
      await refresh({ clearError: false }).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const onRetry = async (doc) => {
    setBusy(true);
    setError(null);
    setNotice(`Indexing “${doc.title}”…`);
    try {
      const result = await triggerIngest(doc.id, null, { retries: 2, retryDelayMs: 300 });
      await refresh({ clearError: false });
      setNotice(
        result?.status === 'ready'
          ? `“${doc.title}” is ready.`
          : null
      );
    } catch (err) {
      setNotice(null);
      setError(err.message || 'Re-ingest failed');
      await refresh({ clearError: false }).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Remove “${doc.title}”? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteOrgDocument(doc);
      await refresh({ clearError: true });
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-pp-line bg-pp-surface p-6 shadow-pp" data-tour="settings-bylaws">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <FileText className="h-4 w-4 text-pp-accent" />
            Bylaws &amp; special rules
          </h2>
          <p className="mt-1 text-sm text-pp-muted">
            Upload governing documents the chair can query during a meeting. These do not change motion
            buttons or procedure in the app—answers are for clarification only.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh({ clearError: true })}
          disabled={loading || busy}
          className="inline-flex items-center gap-1.5 rounded-lg border border-pp-line px-3 py-1.5 text-xs font-medium disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}
      {notice && !error && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {notice}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-pp-muted">Document type</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="mt-1 rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
          >
            {DOC_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-pp-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          <Upload className="h-4 w-4" />
          {busy ? 'Working…' : 'Upload PDF / DOCX / TXT'}
          <input
            type="file"
            accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
            className="sr-only"
            disabled={busy}
            onChange={(e) => void onFileChange(e)}
          />
        </label>
        <button
          type="button"
          onClick={() => setShowPaste((v) => !v)}
          className="rounded-xl border border-pp-line px-4 py-2 text-sm font-medium"
        >
          {showPaste ? 'Hide paste' : 'Paste text'}
        </button>
      </div>

      {showPaste && (
        <div className="mt-4 space-y-3 rounded-xl border border-pp-line bg-pp-paper p-4">
          <div>
            <label className="block text-xs font-medium text-pp-muted">Title</label>
            <input
              type="text"
              value={pasteTitle}
              onChange={(e) => setPasteTitle(e.target.value)}
              className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-pp-muted">
              Text (need ~200+ characters for indexing)
            </label>
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={8}
              className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              placeholder="Paste bylaws or special rules here…"
            />
          </div>
          <button
            type="button"
            disabled={busy || pasteText.trim().length < 50}
            onClick={() => void onPasteSubmit()}
            className="rounded-xl bg-pp-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Save pasted document
          </button>
        </div>
      )}

      <div className="mt-5">
        {loading && docs.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-pp-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading documents…
          </p>
        ) : docs.length === 0 ? (
          <p className="text-sm text-pp-muted">
            No documents yet. Upload bylaws so the meeting assistant can cite your rules.
          </p>
        ) : (
          <ul className="divide-y divide-pp-line rounded-xl border border-pp-line">
            {docs.map((doc) => (
              <li key={doc.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{doc.title}</p>
                  <p className="mt-0.5 text-xs text-pp-muted">
                    {DOC_KINDS.find((k) => k.value === doc.kind)?.label || doc.kind}
                    {' · '}
                    <span
                      className={
                        doc.status === 'ready'
                          ? 'text-emerald-700'
                          : doc.status === 'failed'
                            ? 'text-rose-700'
                            : 'text-amber-700'
                      }
                    >
                      {STATUS_LABEL[doc.status] || doc.status}
                    </span>
                    {doc.extracted_char_count
                      ? ` · ${doc.extracted_char_count.toLocaleString()} chars`
                      : ''}
                  </p>
                  {doc.error_message && (
                    <p className="mt-1 text-xs text-rose-700">{doc.error_message}</p>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  {(doc.status === 'failed' || doc.status === 'pending') && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void onRetry(doc)}
                      className="rounded-lg border border-pp-line px-2.5 py-1.5 text-xs font-medium"
                    >
                      Retry
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onDelete(doc)}
                    className="rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs text-rose-700"
                    aria-label={`Delete ${doc.title}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
